// Device-handoff store (Feature 42). Shared state for the two surfaces that
// show "playing on another device": the full pill (HandoffBanner, desktop home
// only) and the always-reachable header icon (HandoffIndicator). Sharing the
// poll + dismissed set here means they never double-poll and a dismiss in one
// place hides it in both. Mirrors the jobs store's ref-counted poller.
import { defineStore } from "pinia";
import { listActive } from "@/services/activePlayback";
import { useAuthStore } from "@/stores/auth";

// Only navigate to in-app paths — the host-supplied route comes from another
// household device, but still guard against an open-redirect: a single leading
// "/" only (reject "//evil.com" and scheme URLs).
export function isLocalRoute(r) {
  return typeof r === "string" && r.startsWith("/") && !r.startsWith("//");
}

function sessionKey(s) {
  return `${s.deviceId}:${s.fileKey}`;
}

export const useHandoffStore = defineStore("handoff", {
  state: () => ({
    sessions: [], // foreign playback sessions (other devices on this invite)
    dismissed: [], // session keys the user dismissed this session
    _timer: null,
    _subscribers: 0,
  }),
  getters: {
    // Non-dismissed foreign sessions that target a safe local route.
    activeSessions: (s) =>
      s.sessions.filter(
        (x) =>
          x &&
          x.route &&
          isLocalRoute(x.route) &&
          !s.dismissed.includes(sessionKey(x)),
      ),
    hasActive() {
      return this.activeSessions.length > 0;
    },
  },
  actions: {
    async refresh() {
      // Hidden tab → skip the poll; the interval self-heals on the next
      // visible tick (no point burning requests nobody can see).
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      )
        return;
      const auth = useAuthStore();
      if (!auth.isUnlocked) {
        this.sessions = [];
        return;
      }
      // listActive() swallows its own errors and returns [].
      this.sessions = await listActive();
    },
    dismiss(s) {
      const k = sessionKey(s);
      if (!this.dismissed.includes(k)) this.dismissed = [...this.dismissed, k];
    },
    startPolling(intervalMs = 15000) {
      this._subscribers += 1;
      if (this._timer) return;
      this.refresh();
      this._timer = setInterval(() => this.refresh(), intervalMs);
    },
    stopPolling() {
      this._subscribers = Math.max(0, this._subscribers - 1);
      if (this._subscribers === 0 && this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    },
  },
});
